import React, { useMemo } from 'react';
import classNames from 'classnames';
import styles from './ReportReasonBreakdown.module.scss';
import { useTranslation } from 'react-i18next';

export interface ReportReasonItem {
    reason: string;
    count: number;
    color: string;
}

interface ReportReasonBreakdownProps {
    data?: Array<{
        count: number;
        reasonCode: string;
        reasonLabel: string;
    }>;
    className?: string;
}

const REASON_COLOR_MAP: Record<string, string> = {
    'copyright_legal': '#f4511e',
    'violence': '#e53935',
    'hate_harassment': '#c62828',
    'spam_scam': '#ffb300',
    'misinformation': '#1e88e5',
    'child_safety': '#6a1b9a',
    'sexual_content': '#e91e63',
};

const ReportReasonBreakdown: React.FC<ReportReasonBreakdownProps> = ({
    data = [],
    className
}) => {
    const { t } = useTranslation(['user', 'video', 'common', 'upload']);

    // Transform API data to component format
    const transformedData = useMemo((): ReportReasonItem[] => {
        if (!data || data.length === 0) return [];
        
        return data.map((item) => ({
            reason: item.reasonLabel,
            count: item.count,
            color: REASON_COLOR_MAP[item.reasonCode] || '#999999', // Default color fallback
        }));
    }, [data]);

    const maxCount = useMemo(() => {
        if (!transformedData || transformedData.length === 0) return 0;
        return Math.max(...transformedData.map((item) => item.count));
    }, [transformedData]);

    const getBarWidth = (count: number): number => {
        if (!maxCount || maxCount === 0) return 0;
        return (count / maxCount) * 100;
    };

    return (
        <div className={classNames(styles.container, className)}>
            <h3 className={styles.title}>{t('user:report_details.report_reason_breakdown')}</h3>

            <div className={styles.reasonsList}>
                {transformedData.map((item, index) => (
                    <div key={`${item.reason}-${index}`} className={styles.reasonRow}>
                        <div className={styles.reasonLabel}>
                            <span className={styles.reasonText}>{item.reason}</span>
                        </div>

                        <div className={styles.barContainer}>
                            <div
                                className={styles.bar}
                                style={{
                                    width: `${getBarWidth(item.count)}%`,
                                    backgroundColor: item.color,
                                }}
                            />
                        </div>

                        <div className={styles.countLabel}>
                            <span className={styles.countText}>{item.count}</span>
                            <span className={styles.countUnit}>{t('user:report_details.count_text')}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ReportReasonBreakdown;